import { datadogLogs } from '@datadog/browser-logs'
import axios from 'axios'

type Operation = 'query' | 'mutation'
type QueryLikeKey = readonly unknown[] | undefined

type FailureContext = {
  errorId: string
  operation: Operation
  family: string
  errorType: string
  axiosCode?: string
  httpStatus?: number
  requestId?: string
}

const SAFE_REQUEST_ID = /^[A-Za-z0-9._:-]{1,128}$/
const REQUEST_ID_HEADERS = ['x-request-id', 'x-correlation-id'] as const
const FAILURE_THROTTLE_MS = 60_000
const MAX_TRACKED_FAILURES = 200
const lastLoggedFailureAt = new Map<string, number>()

// These labels are the bounded React Query family roots currently used by the
// app. The focused test documents them against the currently established
// query-key roots.
export const QUERY_DIAGNOSTIC_FAMILIES = [
  'additionalfields',
  'agent',
  'apikeys',
  'automationSettings',
  'brand_suggestions',
  'budgetrequests',
  'bulkInvite',
  'cardFeedSavedViews',
  'cardfeeds',
  'charts',
  'countries',
  'creditCardStatistics',
  'creditcards',
  'creditcardsv2',
  'csp',
  'dynamicTile',
  'expensedata',
  'features',
  'g2',
  'googleWallet',
  'issuers',
  'mccgroups',
  'notifications',
  'notificationsettings',
  'oope',
  'organizations',
  'orgexpensereports',
  'orgintegrations',
  'phoneVerification',
  'pricingplans',
  'receiptAttachments',
  'receiptManagement',
  'reportDownloads',
  'reporting',
  'rules',
  'savedviews',
  'sessionConfig',
  'statistics',
  'transaction-accounting-statistics',
  'transaction-counts',
  'transaction-details',
  'transaction-expense-category-labels',
  'transaction-lists',
  'transaction-mutations',
  'userneedsapprovalstats',
  'users',
  'usertaskstats',
  'virtualCardFormCreditCards',
  'virtualcards',
] as const
const SAFE_QUERY_FAMILIES = new Set<string>(QUERY_DIAGNOSTIC_FAMILIES)
const SAFE_AXIOS_CODES = new Set([
  'ERR_BAD_OPTION_VALUE',
  'ERR_BAD_OPTION',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_NETWORK',
  'ERR_FR_TOO_MANY_REDIRECTS',
  'ERR_DEPRECATED',
  'ERR_BAD_RESPONSE',
  'ERR_BAD_REQUEST',
  'ERR_CANCELED',
  'ERR_NOT_SUPPORT',
  'ERR_INVALID_URL',
])
const EXCLUDED_HTTP_STATUSES = new Set([401])
const EXCLUDED_AXIOS_CODES = new Set(['ERR_CANCELED'])

const safeFamily = (key: QueryLikeKey): string => {
  for (const candidate of key ?? []) {
    if (typeof candidate === 'string' && SAFE_QUERY_FAMILIES.has(candidate)) {
      return candidate
    }
  }

  // React Query mutation keys are optional. Keep their intentionally keyless
  // failures in one bounded dimension instead of guessing from variables.
  return 'unknown'
}

const safeErrorType = (error: unknown): string => {
  if (axios.isAxiosError(error)) return 'AxiosError'
  if (error instanceof EvalError) return 'EvalError'
  if (error instanceof RangeError) return 'RangeError'
  if (error instanceof ReferenceError) return 'ReferenceError'
  if (error instanceof SyntaxError) return 'SyntaxError'
  if (error instanceof TypeError) return 'TypeError'
  if (error instanceof URIError) return 'URIError'
  return error instanceof Error ? 'Error' : typeof error
}

const safeRequestId = (headers: unknown): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined

  const get = (headers as { get?: (name: string) => unknown }).get
  for (const name of REQUEST_ID_HEADERS) {
    const matchingName =
      typeof get === 'function'
        ? undefined
        : Object.keys(headers as Record<string, unknown>).find(
            (key) => key.toLowerCase() === name,
          )
    const value =
      typeof get === 'function'
        ? get.call(headers, name)
        : matchingName
          ? (headers as Record<string, unknown>)[matchingName]
          : undefined
    if (typeof value === 'string' && SAFE_REQUEST_ID.test(value)) return value
  }

  return undefined
}

const isRoutineAxiosFailure = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (EXCLUDED_AXIOS_CODES.has(error.code ?? '') ||
    EXCLUDED_HTTP_STATUSES.has(error.response?.status ?? -1))

export const logReactQueryFailure = (
  operation: Operation,
  error: unknown,
  key: QueryLikeKey,
): void => {
  try {
    if (isRoutineAxiosFailure(error)) return

    const errorId = `react_query.${operation}_failed`
    const context: FailureContext = {
      errorId,
      operation,
      family: safeFamily(key),
      errorType: safeErrorType(error),
    }

    if (axios.isAxiosError(error)) {
      if (typeof error.code === 'string' && SAFE_AXIOS_CODES.has(error.code)) {
        context.axiosCode = error.code
      }
      if (
        typeof error.response?.status === 'number' &&
        Number.isInteger(error.response.status) &&
        error.response.status >= 100 &&
        error.response.status <= 599
      ) {
        context.httpStatus = error.response.status
      }

      const requestId = safeRequestId(error.response?.headers)
      if (requestId) context.requestId = requestId
    }

    let signature: string | undefined
    let now: number | undefined
    if (context.axiosCode === 'ERR_NETWORK') {
      signature = [
        context.errorId,
        context.family,
        context.errorType,
        context.httpStatus,
      ].join(':')
      now = Date.now()
      const lastLoggedAt = lastLoggedFailureAt.get(signature)
      if (
        lastLoggedAt !== undefined &&
        now - lastLoggedAt < FAILURE_THROTTLE_MS
      ) {
        return
      }
    }

    datadogLogs.logger.error(errorId, context)
    if (signature !== undefined && now !== undefined) {
      if (lastLoggedFailureAt.size >= MAX_TRACKED_FAILURES) {
        lastLoggedFailureAt.clear()
      }
      lastLoggedFailureAt.set(signature, now)
    }
  } catch {
    // Telemetry must never alter query or mutation control flow.
  }
}
